import { useState, useEffect, useMemo, useCallback } from 'react';
import moment from 'moment';

export type InterviewStatus = 'upcoming' | 'ongoing' | 'completed' | 'none';

interface UseInterviewStatusProps {
  fromDate?: string | Date | null;
  toDate?: string | Date | null;
}

export const useInterviewStatus = ({ fromDate, toDate }: UseInterviewStatusProps): InterviewStatus => {
  const startMoment = useMemo(() => (fromDate ? moment(fromDate) : null), [fromDate]);
  const endMoment = useMemo(() => (toDate ? moment(toDate) : null), [toDate]);

  const calculateStatus = useCallback((): InterviewStatus => {
    if (!startMoment || !endMoment) return 'none';
    const now = moment();

    if (now.isSameOrAfter(endMoment)) return 'completed';
    if (now.isSameOrAfter(startMoment)) return 'ongoing';
    return 'upcoming';
  }, [startMoment, endMoment]);

  const [status, setStatus] = useState<InterviewStatus>(calculateStatus);

  useEffect(() => {
    if (!startMoment || !endMoment) {
      setStatus('none');
      return;
    }

    const updateStatus = () => setStatus(calculateStatus());
    updateStatus();

    const now = moment();
    const timeUntilStart = startMoment.diff(now);
    const timeUntilEnd = endMoment.diff(now);

    const timers: NodeJS.Timeout[] = [];

    // Schedule transition to 'ongoing' when start time hits
    if (timeUntilStart > 0) {
      timers.push(setTimeout(updateStatus, timeUntilStart));
    }

    // Schedule transition to 'completed' when end time hits
    if (timeUntilEnd > 0) {
      timers.push(setTimeout(updateStatus, timeUntilEnd));
    }

    // eslint-disable-next-line consistent-return
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [startMoment, endMoment, calculateStatus]);

  return status;
};
